import session from '@/app/providers/session';
import { GraphQLClient, Variables } from 'graphql-request';
import { Client, createClient, fetchExchange, RequestPolicy, subscriptionExchange } from '@urql/core';
import { createClient as createWSClient } from 'graphql-ws';
import { HTTP_STATUS_CODE } from '@/app/axios/ChekResponseStatus';
import { nativeApp } from '@/shared/services/native-app';
import { buildSentryAuthContext, SentryLevel, useSentry } from '@/shared/lib/sentry';
import { useAuthUserStore } from '@/app/stores/user';

function extractServiceName(serviceUrl?: string) {
  if (!serviceUrl) return 'unknown-service';

  try {
    const { pathname } = new URL(serviceUrl);
    const match = pathname.match(/\/mobile\/([^/]+)\/graphql$/);
    return match?.[1] ?? serviceUrl;
  } catch {
    const match = serviceUrl.match(/\/mobile\/([^/]+)\/graphql$/);
    return match?.[1] ?? serviceUrl;
  }
}

function reportGraphqlError(document: any, variables: any, error: any) {
  const operationDefinition = document?.definitions?.[0];
  const graphqlField = operationDefinition?.selectionSet?.selections?.[0]?.name?.value;
  const authUserStore = useAuthUserStore();
  const payload = {
    graphqlField,
    payload: { ...variables },
    service: extractServiceName(error?.response?.url),
    error: {
      errors: { ...error?.graphQLErrors },
    },
    level: SentryLevel.ERROR,
    typeAction: operationDefinition?.name?.value,
    xTraceId: error?.response?.headers?.get?.('x-trace-id'),
    headerClassification: error?.response?.headers?.get?.('x-error-classification') ?? undefined,
    statusCode: error?.response?.status,
    ...buildSentryAuthContext(authUserStore),
  };
  useSentry(payload);
}

export default class UrqlClient extends GraphQLClient {
  private readonly client: Client;
  private readonly fetchOptions: RequestInit;

  constructor(url: string) {
    super(url);
    this.fetchOptions = {
      credentials: 'include',
      headers: session.applyDeviceIdHeaders() as unknown as HeadersInit,
    };
    const wsClient = createWSClient({
      url: url.replace('https://', 'wss://').replace('graphql', 'subscriptions'),
      // shouldRetry: () => true,
      // retryAttempts: Infinity,
      // retryWait: async () => new Promise((r) => setTimeout(r, 5000)),
    });
    this.client = createClient({
      url,
      fetch: async (...args) => {
        return await fetch(...args);
      },
      //@ts-ignore
      fetchOptions: this.fetchOptions,
      exchanges: [
        fetchExchange,
        subscriptionExchange({
          forwardSubscription(request) {
            const input = { ...request, query: request.query || '' };
            return {
              subscribe(sink) {
                const unsubscribe = wsClient.subscribe(input, sink);
                return { unsubscribe };
              },
            };
          },
        }),
      ],
    });
  }

  public async request<T = any, V extends Variables = Variables>(documentOrOptions: any, variables?: V): Promise<T> {
    const isRequestOptions =
      typeof documentOrOptions === 'object' && documentOrOptions !== null && 'document' in documentOrOptions;
    const document = isRequestOptions ? documentOrOptions.document : documentOrOptions;
    const requestVariables = isRequestOptions ? documentOrOptions.variables : variables;
    const signal = isRequestOptions ? documentOrOptions.signal : undefined;
    const context = signal ? { fetchOptions: { ...this.fetchOptions, signal } } : undefined;

    if (document.definitions[0]?.operation === 'mutation') {
      const result = await this.client.mutation<T>(document, requestVariables, context).toPromise();
      if (result.error?.response?.status === HTTP_STATUS_CODE.Unauthorized) nativeApp.sessionExpired();

      if (result.error) {
        if (signal?.aborted) throw result.error;
        const headers = result.error?.response?.headers;
        const errorClassification = headers?.get?.('x-error-classification');
        if (errorClassification === 'UnauthorizedException') nativeApp.sessionExpired();
        reportGraphqlError(document, variables, result.error);
        throw result.error;
      }
      return result.data!;
    }

    if (document.definitions[0]?.operation === 'subscription') {
      const { subscribe } = this.client.subscription(document, variables);
      // @ts-ignore
      return subscribe;
    }

    const requestPolicy: string | null = 'network-only';
    const result = await this.client
      .query<T>(document, requestVariables, {
        requestPolicy: requestPolicy as RequestPolicy,
        ...context,
      })
      .toPromise();
    if (result.error?.response?.status === HTTP_STATUS_CODE.Unauthorized) nativeApp.sessionExpired();

    if (result.error) {
      if (signal?.aborted) throw result.error;
      const headers = result.error?.response?.headers;
      const errorClassification = headers?.get?.('x-error-classification');
      if (errorClassification === 'UnauthorizedException') nativeApp.sessionExpired();

      reportGraphqlError(document, variables, result.error);
      throw result.error;
    }
    return result.data!;
  }
}
